// deps
import React, { memo, useCallback, useContext, useMemo, useState } from "react";

// internal-deps
import { format, isEmpty, useEffectOnce, isAppPlatform, isAndroidPlatform } from "@swiggy-private/core-ui";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// components
import { CalloutTag } from "./components/callout-tag";
import { SuccessBanner } from "./components/payment-success-banner";
import { BillDetails } from "./components/payment-success-bills-details";
import { Footer } from "./components/payment-success-footer";
import { Header } from "./components/payment-success-header";
import { SavingsCard } from "./components/payment-success-savings-card";
import { OneBenefitsCard } from "./components/payment-success-one-benefits-card";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import SplitBillHalfCardContent from "./components/SplitBillHalfCard";
import SplitBillWidget from "./components/SplitBillWidget";

// styles
import {
    AnimatedScrollableContent,
    BillDetailsWrapper,
    CashbackCallout,
    ContentDiv,
    DineoutWrapper,
    HeaderContainer,
    OneAndBillWrapper,
    PaidImageWrapper,
    ScrollableContent,
    SplitBillWidgetAndImageWrapper,
} from "./styles";

// helpers
import { logSuccessPageViewedEvent } from "@dineout-payment-success-ui/helpers/Clevertap";
import {
    navigateToLink,
    openHelpWebView,
    postOrderEventHelper,
} from "@dineout-payment-success-ui/helpers/nativeBridge";
import { getDOPaymentSuccessData } from "@dineout-payment-success-ui/helpers/PaymentSuccessHelper";
import Blaze from "@swiggy-private/blaze.js";

// types
import { IDineoutPaymentSuccess } from "@dineout-payment-success-ui/interfaces";
import {
    BLAZE_EVENTS,
    GlobalHeaderState,
    NEWRELIC_TABLES,
    recordNREvent,
    UserContext,
    getUserFromUserContextValue,
} from "@swiggy-private/checkout-core";
import { Order } from "@swiggy-private/payment-types";

// hooks
import { useDineoutPaymentSuccess } from "@dineout-payment-success-ui/hooks/useDineoutPaymentSuccess";
import { useSplitBill } from "@dineout-payment-success-ui/hooks/useSplitBill";

// constants
import {
    ANALYTICS_CONSTANTS,
    BILL_TEXTS,
    CASHBACK_CALLOUT,
    DINEOUT_HOMEPAGE_WITH_REFRESH_DEEPLINK,
} from "@dineout-payment-success-ui/constants";
import { MerchCarouselProps, SwgyHeaderKeysList } from "@swiggy-private/checkout-types";
import MerchCarousel from "./components/MerchCarousel";

//analytics
import { PAYMENT_SUCCESS_FOOTER } from "@swiggy-private/payment-test-ids";
import { SplitMethod } from "../../interfaces";

const SuccessPage: React.FC<
    IDineoutPaymentSuccess & { postOrderState: boolean } & {
        carouselData: MerchCarouselProps;
    }
> = (props) => {
    const { rawResponse, carouselData, postOrderState } = props;

    const orderData = props?.data?.orders?.[0];
    const confirmOrderData = orderData?.orderJobs?.[0];
    const metadataString = confirmOrderData?.metadata || "{}";
    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform);
    const {
        restaurantInfo,
        successBannerInfo,
        oneBenefitsCardInfo,
        billDetailsInfo,
        paidImage,
        restaurantId,
        dealCategory,
        styles,
        cashbackInfoText,
        savingCards,
        splitPayWidget,
    } = getDOPaymentSuccessData(metadataString);

    const {
        onClickCloseDineoutPaymentSuccess,
        onClickViewHelpDineoutPaymentSuccess,
        onImpressionDineoutPaymentSuccess,
        onImpressionSplitBillWidget,
        onClickSplitBillWidget,
    } = useDineoutPaymentSuccess();

    // Get user from context
    const { user: userContextValue } = useContext(UserContext);
    const user = useMemo(() => getUserFromUserContextValue(userContextValue), [userContextValue]);

    // Get bill amount and user info - use paidAmount from successBannerInfo as it reflects actual amount paid
    const billAmount = Number(successBannerInfo?.paidAmount) || Number(splitPayWidget?.amount);
    const totalBillAmount = Number(successBannerInfo?.totalAmount) || billAmount;
    const userName = splitPayWidget?.userName;
    const userId = user?.userId || "";

    const [splitBillKeyboardHeight, setSplitBillKeyboardHeight] = useState(0);

    // Split bill hook
    const {
        splitBillContacts,
        showSplitBillHalfCard,
        splitBillLoading,
        splitBillSuccess,
        splitBillSuccessData,
        currentSplitWidgetState,
        splitBillError,
        handleSplitBillClick,
        handleAddMoreGuests,
        handleCloseSplitBillHalfCard,
        handleRetrySplitBill,
        handleSplitBillConfirm,
    } = useSplitBill({
        orderId: orderData?.orderId,
        userName,
        userId,
    });

    const navigateToDineoutHomePage = useCallback(
        (label?: string) => {
            const rawOrderData = rawResponse?.data ?? {};
            onClickCloseDineoutPaymentSuccess(label, orderData?.orderId);
            if (!isEmpty(postOrderState)) {
                postOrderEventHelper(rawOrderData?.order_data, true);
            }
            navigateToLink(DINEOUT_HOMEPAGE_WITH_REFRESH_DEEPLINK, true, true);
        },
        [onClickCloseDineoutPaymentSuccess, orderData?.orderId, postOrderState, rawResponse?.data],
    );

    const handleViewHelp = useCallback(() => {
        onClickViewHelpDineoutPaymentSuccess(orderData?.orderId || "");
        openHelpWebView(orderData?.orderId || "", restaurantId || "");
    }, [onClickViewHelpDineoutPaymentSuccess, orderData?.orderId, restaurantId]);

    const handleSplitBillClickWithAnalytics = useCallback(() => {
        onClickSplitBillWidget({
            orderId: orderData?.orderId || "",
            totalBillAmount: totalBillAmount,
            amountPaidByUser: billAmount,
            restaurantId: restaurantId || "",
        });
        handleSplitBillClick();
    }, [onClickSplitBillWidget, handleSplitBillClick, orderData?.orderId, totalBillAmount, billAmount, restaurantId]);

    const handleSplitBillWidgetImpression = useCallback(() => {
        onImpressionSplitBillWidget({
            orderId: orderData?.orderId || "",
            totalBillAmount: totalBillAmount,
            amountPaidByUser: billAmount,
            restaurantId: restaurantId || "",
        });
    }, [onImpressionSplitBillWidget, orderData?.orderId, totalBillAmount, billAmount, restaurantId]);

    useEffectOnce(() => {
        Blaze.recordEnd(BLAZE_EVENTS.RECORD_DO_PAYMENT_SUCCESS_SCREEN_LOAD_TIME);
        recordNREvent({
            eventType: NEWRELIC_TABLES.DO_PAYMENT_SUCCESS_PAGE_VIEW,
        });
        if (typeof window !== "undefined") {
            window.handleHardwareBack = navigateToDineoutHomePage;
        }

        if (!isEmpty(orderData?.orderId) && typeof onImpressionDineoutPaymentSuccess === "function") {
            logSuccessPageViewedEvent(orderData as Order);
            onImpressionDineoutPaymentSuccess({ orderId: orderData?.orderId, dealCategory: dealCategory || "" });
        }
        return () => {
            if (typeof window !== "undefined") {
                window.handleHardwareBack = () => void 0;
            }
        };
    });
    const isApp = isAppPlatform(platform);
    const isAndroid = isAndroidPlatform(platform);
    const isCarouselDataAvailable = !!carouselData && !!carouselData.gridElements;
    const isSavingAmountExists = Number(oneBenefitsCardInfo?.numericValue) > 0;
    const isSavingsOrCarouselAvailable = isSavingAmountExists || isCarouselDataAvailable;
    const isCalloutExists = Boolean(oneBenefitsCardInfo?.calloutInfo && oneBenefitsCardInfo?.calloutInfo?.imageUrl);
    const isSplitBillWidgetAvailable = !isEmpty(splitPayWidget);
    const renderMerchCarousel = isCarouselDataAvailable ? (
        <MerchCarousel {...carouselData} isCalloutExists={isCalloutExists} />
    ) : null;
    return (
        <DineoutWrapper>
            <AnimatedScrollableContent>
                <ScrollableContent isApp={isApp}>
                    <ContentDiv>
                        <HeaderContainer isAndroid={isAndroid}>
                            <Header
                                title={restaurantInfo?.restaurantName}
                                description={restaurantInfo?.restaurantAddress}
                                onCloseButtonClick={() => navigateToDineoutHomePage(ANALYTICS_CONSTANTS.CLOSE)}
                            />

                            <SuccessBanner
                                totalAmount={successBannerInfo?.totalAmount}
                                paidAmount={successBannerInfo?.paidAmount}
                                tip={successBannerInfo?.tip}
                                subscriptionValue={successBannerInfo?.subscriptionValue}
                                backgroundImage={successBannerInfo?.backgroundImage}
                                styles={styles}
                            />
                        </HeaderContainer>

                        <OneAndBillWrapper
                            isMarginTop={Boolean(successBannerInfo?.tip || successBannerInfo?.subscriptionValue)}
                            data-testid="One-bill-wrapper"
                        >
                            <SplitBillWidgetAndImageWrapper>
                                <SplitBillWidget
                                    splitPayWidget={splitPayWidget}
                                    splitBillSuccess={splitBillSuccess}
                                    currentSplitWidgetState={currentSplitWidgetState}
                                    onClick={handleSplitBillClickWithAnalytics}
                                    onImpression={handleSplitBillWidgetImpression}
                                />
                                <PaidImageWrapper
                                        isCalloutExists={isCalloutExists}
                                        isSavingsAvailable={isSavingsOrCarouselAvailable}
                                        isSavingCardsAvailable={!isEmpty(savingCards)}
                                    >
                                        <Image src={paidImage} />
                                </PaidImageWrapper>
                            </SplitBillWidgetAndImageWrapper>
                            {oneBenefitsCardInfo?.savingsValue && !savingCards ? (
                                <OneBenefitsCard
                                    savingsValue={oneBenefitsCardInfo?.savingsValue}
                                    userStatus={oneBenefitsCardInfo?.userStatus}
                                    oneLogoStyle={oneBenefitsCardInfo?.oneLogoStyle?.paymentScreenOneStyle}
                                    oneLogoUrl={oneBenefitsCardInfo?.oneLogoUrl}
                                    successAnimationData={oneBenefitsCardInfo?.successAnimationData}
                                    styles={styles}
                                    orderId={orderData?.orderId}
                                />
                            ) : null}
                            {!isEmpty(savingCards) ? (
                                <SavingsCard title={savingCards.title} cards={savingCards.cards} />
                            ) : null}
                            {isCalloutExists ? (
                                <CalloutTag
                                    info={oneBenefitsCardInfo.calloutInfo}
                                    isSavingsAvailable={isSavingAmountExists}
                                    isSavingCardsAvailable={!isEmpty(savingCards)}
                                    styles={styles}
                                />
                            ) : null}
                            {renderMerchCarousel}
                            <BillDetailsWrapper
                                isCalloutExists={isCalloutExists}
                                isSavingsAvailable={isSavingsOrCarouselAvailable}
                                isSavingCardsAvailable={!isEmpty(savingCards)}
                                isSplitBillWidgetAvailable={isSplitBillWidgetAvailable}
                            >
                                <BillDetails
                                    billEntries={billDetailsInfo}
                                    title={format(BILL_TEXTS.BILL_ORDER_ID, orderData?.orderId || "")}
                                />
                                {cashbackInfoText && (
                                    <CashbackCallout data-testid={PAYMENT_SUCCESS_FOOTER.CASHBACK_CALLOUT}>
                                        {format(CASHBACK_CALLOUT, cashbackInfoText)}
                                    </CashbackCallout>
                                )}
                            </BillDetailsWrapper>
                        </OneAndBillWrapper>
                    </ContentDiv>
                </ScrollableContent>
                <Footer
                    doneClickHandler={() => navigateToDineoutHomePage(ANALYTICS_CONSTANTS.DONE)}
                    viewHelpClickHandler={handleViewHelp}
                />
            </AnimatedScrollableContent>
            {showSplitBillHalfCard && (
                <HalfCard
                    isOpen={showSplitBillHalfCard}
                    onClose={handleCloseSplitBillHalfCard}
                    hasCustomPadding={true}
                    hideCloseIcon
                    positionFromBottom={splitBillKeyboardHeight}
                >
                    <SplitBillHalfCardContent
                        billAmount={billAmount}
                        splitBillContacts={splitBillContacts}
                        splitModeOptions={splitPayWidget?.splitModeOptions || []}
                        dineCashSplit={splitPayWidget?.dineCashSplit}
                        defaultSplitModeSelection={splitPayWidget?.defaultSplitModeSelection || SplitMethod.AMOUNT}
                        onConfirm={handleSplitBillConfirm}
                        handleClose={handleCloseSplitBillHalfCard}
                        onAddMoreGuests={handleAddMoreGuests}
                        splitBillLoading={splitBillLoading}
                        splitBillSuccess={splitBillSuccess}
                        splitBillSuccessData={splitBillSuccessData}
                        splitBillError={splitBillError}
                        onRetrySplitBill={handleRetrySplitBill}
                        orderId={orderData?.orderId}
                        restaurantId={restaurantId}
                        onKeyboardStateChange={setSplitBillKeyboardHeight}
                    />
                </HalfCard>
            )}
        </DineoutWrapper>
    );
};

export default memo(SuccessPage);
